<template>
  <div class="pengaturan-umum-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Pengaturan Umum</h1>
      <p class="text-gray-600">Kelola pengaturan umum aplikasi dan informasi dasar</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Informasi Aplikasi Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Informasi Aplikasi</h2>
        
        <el-form 
          :model="generalSettings" 
          label-width="180px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Nama Aplikasi">
            <el-input 
              v-model="generalSettings.appName" 
              placeholder="Masukkan nama aplikasi"
              :disabled="saving.general"
            />
          </el-form-item>

          <el-form-item label="Deskripsi">
            <el-input 
              v-model="generalSettings.appDescription" 
              type="textarea" 
              :rows="4"
              placeholder="Masukkan deskripsi aplikasi"
              :disabled="saving.general"
            />
          </el-form-item>

          <el-form-item label="Email Kontak">
            <el-input 
              v-model="generalSettings.contactEmail" 
              placeholder="email@example.com"
              type="email"
              :disabled="saving.general"
            />
            <div class="form-hint">Email yang akan digunakan untuk kontak support</div>
          </el-form-item>

          <el-form-item label="Nomor Telepon">
            <el-input 
              v-model="generalSettings.phoneNumber" 
              placeholder="+62 xxx xxxx xxxx"
              :disabled="saving.general"
            />
          </el-form-item>

          <el-form-item label="Alamat Kantor">
            <el-input 
              v-model="generalSettings.address" 
              type="textarea"
              :rows="3"
              placeholder="Masukkan alamat lengkap kantor"
              :disabled="saving.general"
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- Mode Maintenance Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Mode Maintenance</h2>
        
        <div class="flex items-start justify-between mb-4">
          <div class="flex-1">
            <p class="text-gray-700 mb-2">
              Status Mode Maintenance
            </p>
            <p class="text-sm text-gray-600">
              Mengaktifkan mode maintenance akan menonaktifkan akses aplikasi untuk semua pengguna kecuali admin
            </p>
          </div>
          
          <div class="ml-4">
            <el-switch
              v-model="generalSettings.maintenanceMode"
              active-text="Aktif"
              inactive-text="Nonaktif"
              active-color="#f56565"
              inactive-color="#48bb78"
              :disabled="saving.general"
            />
          </div>
        </div>

        <div 
          v-if="generalSettings.maintenanceMode" 
          class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <div class="flex items-start">
            <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
            <div>
              <p class="font-medium text-red-800">Mode Maintenance Aktif</p>
              <p class="text-sm text-red-700 mt-1">
                Pengguna tidak dapat mengakses aplikasi. Pastikan untuk menonaktifkan setelah maintenance selesai.
              </p>
            </div>
          </div>
        </div>

        <el-form 
          v-if="generalSettings.maintenanceMode"
          :model="generalSettings" 
          label-width="180px" 
          label-position="left"
          class="settings-form mt-4"
        >
          <el-form-item label="Pesan Maintenance">
            <el-input 
              v-model="generalSettings.maintenanceMessage" 
              type="textarea"
              :rows="3"
              placeholder="Masukkan pesan yang akan ditampilkan kepada pengguna"
              :disabled="saving.general"
            />
            <div class="form-hint">Pesan yang akan ditampilkan saat aplikasi dalam mode maintenance</div>
          </el-form-item>

          <el-form-item label="Estimasi Selesai">
            <el-date-picker
              v-model="generalSettings.maintenanceEndTime"
              type="datetime"
              placeholder="Pilih waktu"
              format="DD/MM/YYYY HH:mm"
              :disabled="saving.general"
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- Pengaturan Lainnya Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Pengaturan Lainnya</h2>
        
        <el-form 
          :model="generalSettings" 
          label-width="180px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Zona Waktu">
            <el-select 
              v-model="generalSettings.timezone" 
              placeholder="Pilih zona waktu"
              :disabled="saving.general"
              class="w-full"
            >
              <el-option label="WIB (GMT+7)" value="Asia/Jakarta" />
              <el-option label="WITA (GMT+8)" value="Asia/Makassar" />
              <el-option label="WIT (GMT+9)" value="Asia/Jayapura" />
            </el-select>
          </el-form-item>

          <el-form-item label="Bahasa Default">
            <el-select 
              v-model="generalSettings.defaultLanguage" 
              placeholder="Pilih bahasa"
              :disabled="saving.general"
              class="w-full"
            >
              <el-option label="Indonesia" value="id" />
              <el-option label="English" value="en" />
            </el-select>
          </el-form-item>

          <el-form-item label="Format Tanggal">
            <el-select 
              v-model="generalSettings.dateFormat" 
              placeholder="Pilih format tanggal"
              :disabled="saving.general"
              class="w-full"
            >
              <el-option label="DD/MM/YYYY" value="DD/MM/YYYY" />
              <el-option label="MM/DD/YYYY" value="MM/DD/YYYY" />
              <el-option label="YYYY-MM-DD" value="YYYY-MM-DD" />
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-700 font-medium">Simpan Perubahan</p>
            <p class="text-sm text-gray-600">Pastikan semua pengaturan sudah benar sebelum menyimpan</p>
          </div>
          
          <div class="flex gap-3">
            <el-button @click="resetSettings" :disabled="saving.general">
              Reset
            </el-button>
            <el-button 
              type="primary" 
              @click="saveGeneralSettings" 
              :loading="saving.general"
            >
              Simpan Perubahan
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Last Updated Info -->
    <div v-if="lastUpdated" class="mt-4 text-center text-sm text-gray-500">
      Terakhir diupdate: {{ formatDate(lastUpdated) }}
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Axios from '@/utils/axiosConfig';

export default {
  name: "PengaturanUmumView",
  data() {
    return {
      loading: false,
      saving: {
        general: false
      },
      // General Settings Data
      generalSettings: {
        appName: '',
        appDescription: '',
        contactEmail: '',
        phoneNumber: '',
        address: '',
        maintenanceMode: false,
        maintenanceMessage: '',
        maintenanceEndTime: null,
        timezone: 'Asia/Jakarta',
        defaultLanguage: 'id',
        dateFormat: 'DD/MM/YYYY'
      },
      originalSettings: {},
      lastUpdated: null
    };
  },
  computed: {
    ...mapState(['host'])
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await Axios.get(`${this.host}/api/settings/general`);
        
        if (response.data) {
          const data = response.data.data || response.data;
          this.generalSettings = {
            ...this.generalSettings,
            ...data
          };
          // Save original settings for reset
          this.originalSettings = JSON.parse(JSON.stringify(this.generalSettings));
          this.lastUpdated = data.updatedAt || null;
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        this.$message.warning('Gagal memuat pengaturan. Menggunakan nilai default.');
      } finally {
        this.loading = false;
      }
    },

    async saveGeneralSettings() {
      // Validation
      if (!this.generalSettings.appName) {
        this.$message.error('Nama aplikasi harus diisi');
        return;
      }

      if (!this.generalSettings.contactEmail) {
        this.$message.error('Email kontak harus diisi');
        return;
      }

      // Email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this.generalSettings.contactEmail)) {
        this.$message.error('Format email tidak valid');
        return;
      }

      if (this.generalSettings.maintenanceMode && !this.generalSettings.maintenanceMessage) {
        this.$message.error('Pesan maintenance harus diisi saat mode maintenance aktif');
        return;
      }

      this.saving.general = true;
      try {
        const response = await Axios.post(`${this.host}/api/settings/general`, this.generalSettings);
        
        if (response.data) {
          this.lastUpdated = new Date();
          this.originalSettings = JSON.parse(JSON.stringify(this.generalSettings));
          this.$message.success('Pengaturan umum berhasil disimpan!');
        }
      } catch (error) {
        console.error('Error saving general settings:', error);
        this.$message.error('Gagal menyimpan pengaturan umum');
      } finally {
        this.saving.general = false;
      }
    },

    resetSettings() {
      this.$confirm('Apakah Anda yakin ingin mereset semua perubahan?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Reset',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        this.generalSettings = JSON.parse(JSON.stringify(this.originalSettings));
        this.$message.info('Pengaturan direset ke nilai sebelumnya');
      }).catch(() => {
        // User cancelled
      });
    },

    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    }
  }
};
</script>

<style scoped>
.pengaturan-umum-container {
  max-width: 1200px;
  margin: 0 auto;
}

.settings-form {
  margin-top: 0;
}

.form-hint {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

/* Customize Element UI form items */
:deep(.el-form-item) {
  margin-bottom: 24px;
}

:deep(.el-form-item__label) {
  color: #374151;
  font-weight: 500;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  border-radius: 8px;
}

:deep(.el-switch) {
  height: auto;
}

:deep(.el-switch__label) {
  font-size: 14px;
}

:deep(.el-select) {
  width: 100%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pengaturan-umum-container {
    padding: 1rem;
  }

  :deep(.el-form-item) {
    flex-direction: column;
  }

  :deep(.el-form-item__label) {
    text-align: left !important;
    width: 100% !important;
    margin-bottom: 8px;
  }

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}
</style>